import React from 'react';
import { Box, Card, CardContent, Typography, Skeleton, alpha } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ScheduleIcon from '@mui/icons-material/Schedule';

export default function ScanSummaryCards({ scanData, loading }) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', gap: 2, mb: 3, width: '100%' }}>
        {[
          { c: '#3b82f6' },
          { c: '#10b981' },
          { c: '#f59e0b' },
          { c: '#8b5cf6' },
        ].map((m, i) => (
          <Card key={i} elevation={0} sx={{
            flex: 1, minWidth: 0, borderRadius: 2, overflow: 'hidden',
            border: '1px solid', borderColor: alpha(m.c, 0.12),
          }}>
            <Box sx={{ height: 3, bgcolor: alpha(m.c, 0.25) }} />
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Skeleton variant="rounded" width={40} height={40} sx={{ borderRadius: 2, flexShrink: 0 }} />
                  <Box>
                    <Skeleton width={60} height={10} sx={{ mb: 0.5, borderRadius: 0.5 }} />
                    <Skeleton width={50} height={22} sx={{ borderRadius: 0.5 }} />
                  </Box>
                </Box>
                <Skeleton variant="rounded" width={44} height={28} sx={{ borderRadius: 1.5 }} />
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    );
  }
  if (!scanData) return null;

  const totalScanned = scanData.scanned ?? scanData.total_scanned ?? 0;
  const signalsFound = scanData.signals ?? scanData.signals_found ?? 0;
  const topScore = scanData.topScore ?? '—';
  const elapsed = (scanData.elapsed ?? scanData.scan_time_s ?? 0);
  const hitRate = totalScanned > 0 ? ((signalsFound / totalScanned) * 100).toFixed(1) : '0';

  const cards = [
    {
      label: 'Đã Quét',
      value: totalScanned,
      badge: `${scanData.strategies_used || 5} chiến lược`,
      icon: <SearchIcon />,
      color: '#3b82f6',
    },
    {
      label: 'Tín Hiệu BUY',
      value: signalsFound,
      badge: `${hitRate}% hit rate`,
      badgeColor: signalsFound > 0 ? '#10b981' : '#94a3b8',
      icon: <SignalCellularAltIcon />,
      color: '#10b981',
    },
    {
      label: 'Top Score',
      value: topScore,
      badge: topScore >= 90 ? 'Excellent' : topScore >= 80 ? 'Good' : '—',
      badgeColor: topScore >= 90 ? '#10b981' : topScore >= 80 ? '#3b82f6' : '#94a3b8',
      icon: <EmojiEventsIcon />,
      color: '#f59e0b',
    },
    {
      label: 'Thời Gian',
      value: `${elapsed.toFixed?.(1) ?? elapsed}s`,
      badge: elapsed < 5 ? 'Nhanh' : elapsed < 15 ? 'TB' : 'Chậm',
      badgeColor: elapsed < 5 ? '#10b981' : elapsed < 15 ? '#f59e0b' : '#ef4444',
      icon: <ScheduleIcon />,
      color: '#8b5cf6',
    },
  ];

  return (
    <Box sx={{ display: 'flex', gap: 2, mb: 3, width: '100%' }}>
      {cards.map((c, i) => (
        <Card key={i} elevation={0} sx={{
          flex: 1, minWidth: 0, borderRadius: 2, overflow: 'hidden', position: 'relative',
          border: '1px solid', borderColor: alpha(c.color, 0.15),
          transition: 'all 0.25s ease',
          '&:hover': {
            borderColor: alpha(c.color, 0.3),
            transform: 'translateY(-2px)',
            boxShadow: `0 6px 20px ${alpha(c.color, 0.12)}`,
          },
        }}>
          <Box sx={{ height: 3, bgcolor: c.color }} />
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              {/* Left: icon + label/value */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                <Box sx={{
                  width: 40, height: 40, borderRadius: 2,
                  bgcolor: c.color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', flexShrink: 0,
                }}>
                  {React.cloneElement(c.icon, { sx: { fontSize: 20 } })}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" fontWeight={600}
                    sx={{ lineHeight: 1, display: 'block', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#94a3b8', mb: 0.3 }}>
                    {c.label}
                  </Typography>
                  <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.1, fontFamily: 'monospace', color: '#1e293b', fontSize: '1.3rem' }}>
                    {typeof c.value === 'number' ? c.value.toLocaleString() : c.value}
                  </Typography>
                </Box>
              </Box>
              {/* Right: badge */}
              {c.badge && c.badge !== '—' && (
                <Box sx={{
                  px: 1, py: 0.5, borderRadius: 1.5, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: alpha(c.badgeColor || c.color, 0.08),
                  border: `1px solid ${alpha(c.badgeColor || c.color, 0.12)}`,
                }}>
                  <Typography variant="caption" sx={{
                    fontWeight: 700, fontSize: '0.6rem', lineHeight: 1, color: c.badgeColor || c.color,
                    whiteSpace: 'nowrap',
                  }}>
                    {c.badge}
                  </Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
}
